import React, { useEffect } from 'react'
import { DoubleImage } from '@christies/react/components/Editorial'
import { debounce } from '@christies/react/utils'
import { withParsedContext, ParsedAnchorValue, ImageValue, SelectValue } from '../../withJSSContext'
import { Theme } from '@christies/react/foundations'
import { track, TrackingEvents } from '@christies/react/utils'
import { RenderFields, getAspectRatio } from '../../utils'
import { QUICK_VIEW_ROOT_ID } from '../../../../helpers/modals'
import { TFunction } from 'i18next'
import { StoredImageParsedValue, useImageStore } from '../../imagesStoreContext'

interface Props {
  parsedFields: {
    'Base Image': ImageValue
    'Lightbox Image': ImageValue
    'Image Description': string
    'Image Type': SelectValue<'8' | '12'>
    'Image Caption': string
    'Image Link': ParsedAnchorValue
    'Right Base Image': ImageValue
    'Right Lightbox Image': ImageValue
    'Right Image Description': string
    'Right Image Link': ParsedAnchorValue
    'Right Image Caption': string
    'Image Fit'?: SelectValue<'contain' | 'cover'>
    'Show on Lightbox': boolean
    'Right Show on Lightbox': boolean
    'Overlay Description': string
    'Right Overlay Description': string
    instanceCount: number | string
    imageCount: number
    'Left Image Interaction Type': string
    'Left Image Interaction Destination': string
    'Left Image Interaction Location': string
    'Screen Reader Caption': string
    'Right Image Interaction Type': string
    'Right Image Interaction Destination': string
    'Right Image Interaction Location': string
    'Right Screen Reader Caption': string
  }
  parsedRouteFields: {
    'Story Theme': SelectValue<Theme | undefined>
  }
  t: TFunction
}

const MagazinesDoubleImageModule: React.FC<Props> = ({ parsedFields, parsedRouteFields, t }) => {
  const { setStoreImage, sharedStoreImages } = useImageStore()
  const currentImageInstance = parsedFields?.imageCount

  const handleTrack = (caption: 'Left' | 'Right'): void => {
    track({
      event: TrackingEvents.INTERACTION,
      data: {
        interaction: {
          type: parsedFields?.[`${caption} Image Interaction Type`],
          location: parsedFields?.[`${caption} Image Interaction Location`],
          destination: parsedFields?.[`${caption} Image Interaction Destination`],
        },
      },
    })
  }

  const leftDescription = parsedFields?.['Image Description']
  const rightDescription = parsedFields?.['Right Image Description']

  const leftBaseImage = {
    ...getAspectRatio({
      ...parsedFields?.['Base Image'],
      alt: parsedFields?.['Screen Reader Caption'] || parsedFields?.['Base Image']?.alt,
    }),
    id: `doubleImage-${currentImageInstance}`,
    description: leftDescription,
  } as StoredImageParsedValue

  const leftLightbox = {
    ...getAspectRatio({
      ...parsedFields?.['Lightbox Image'],
      alt: parsedFields?.['Screen Reader Caption'] || parsedFields?.['Base Image']?.alt,
    }),
    id: `doubleImage-${currentImageInstance}`,
    description: leftDescription,
  } as StoredImageParsedValue

  const rightBaseImage = {
    ...getAspectRatio({
      ...parsedFields?.['Right Base Image'],
      alt: parsedFields?.['Right Screen Reader Caption'] || parsedFields?.['Right Base Image']?.alt,
    }),
    description: rightDescription,
  } as StoredImageParsedValue
  const rightLightbox = {
    ...getAspectRatio({
      ...parsedFields?.['Right Lightbox Image'],
      alt: parsedFields?.['Right Screen Reader Caption'] || parsedFields?.['Right Base Image']?.alt,
    }),
    description: rightDescription,
  } as StoredImageParsedValue

  const imagesFit = parsedFields?.['Image Fit']?.key

  useEffect(() => {
    setStoreImage &&
      setStoreImage(
        currentImageInstance,
        (leftLightbox?.src && leftLightbox) || leftBaseImage,
        (rightLightbox?.src && rightLightbox) || rightBaseImage
      )
  }, [])

  const currentSlide = sharedStoreImages.filter(
    (item) => item?.id === leftLightbox?.id || item?.id === leftBaseImage?.id
  )[0]?.currentSlide

  const handleZoomChange = debounce((value: number): void => {
    track({
      event: TrackingEvents.INTERACTION,
      data: {
        interaction: {
          type: `enlarge image:${value}`,
          location: t('feature-discovery-magazines-doubleimage-analytics-location', {
            defaultValue: 'stories:double image:{{value}}',
            value: `${parsedFields?.instanceCount}`,
          }),
          destination: 'quick view',
        },
      },
    })
  }, 300)

  const leftImage = (leftBaseImage?.src && leftBaseImage) || leftLightbox
  const rightImage = (rightBaseImage?.src && rightBaseImage) || rightLightbox

  const imageProps = {
    leftImage,
    leftDescription,
    leftCta: {
      text: parsedFields?.['Image Caption'],
      href: parsedFields?.['Image Link']?.href,
      target: parsedFields?.['Image Link']?.target,
      onClick: (): void => {
        handleTrack('Left')
      },
    },
    rightImage,
    rightDescription,
    rightCta: {
      text: parsedFields?.['Right Image Caption'],
      href: parsedFields?.['Right Image Link']?.href,
      target: parsedFields?.['Right Image Link']?.target,
      onClick: (): void => {
        handleTrack('Right')
      },
    },
    imagesFit,
    lgWidth: parsedFields?.['Image Type']?.key,
    theme: parsedRouteFields?.['Story Theme']?.key,
    quickViewProps: {
      quickViewRootId: QUICK_VIEW_ROOT_ID,
      title: t('feature-discovery-magazines-quickview-title'),
      ariaLabel: t('feature-discovery-magazines-quickview-arialabel'),
      closeButton: t('feature-discovery-magazines-quickview-closebutton'),
      previousButtonAriaLabel: t('feature-discovery-magazines-quickview-previousbuttonarialabel'),
      nextButtonAriaLabel: t('feature-discovery-magazines-quickview-nextbuttonarialabel'),
      slideDividerLabel: t('feature-discovery-magazines-quickview-sliderpagenumberparticle'),
      onChange: (_e: React.SyntheticEvent | undefined, value: 'previous' | 'next'): void => {
        track({
          event: TrackingEvents.INTERACTION,
          data: {
            interaction: {
              type: `${value} arrow`,
              location: t('feature-discovery-magazines-quickview-analytics-location'),
            },
          },
        })
      },
    },
    quickViewZoomProps: {
      onChange: handleZoomChange,
      rangeSliderAriaLabel: t('feature-discovery-magazines-quickview-rangesliderarialabel'),
      incrementAriaLabel: t('feature-discovery-magazines-quickview-incrementarialabel'),
      decrementAriaLabel: t('feature-discovery-magazines-quickview-decrementarialabel'),
    },
    enableLeftGallery: parsedFields?.['Show on Lightbox'],
    enableRightGallery: parsedFields?.['Right Show on Lightbox'],
    leftOverlayDescription: parsedFields?.['Overlay Description'],
    rightOverlayDescription: parsedFields?.['Right Overlay Description'],
    currentSlide: currentSlide,
    galleryImages: sharedStoreImages,
    t: {
      galleryAccessibilityLabel: t('foundation-discovery-accessibility-images-labels-open-image-in-gallery'),
      linkAccessibilityLabel: t('foundation-discovery-accessibility-images-labels-open-link'),
    },
  }

  return (
    <RenderFields parsedFields={parsedFields}>
      <DoubleImage {...imageProps} />
    </RenderFields>
  )
}

export default withParsedContext(MagazinesDoubleImageModule as React.FC)
